import { DataProvider } from "@opencode-ai/session-ui/context"
import { showToast } from "@/utils/toast"
import { base64Encode } from "@opencode-ai/core/util/encode"
import { useLocation, useNavigate, useParams } from "@solidjs/router"
import {
  type Accessor,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  type ParentProps,
  Show,
} from "solid-js"
import { LoaderV2 } from "@opencode-ai/ui/v2/loader-v2"
import { BrandLogo } from "@/components/brand-logo"
import { useLanguage } from "@/context/language"
import { LocalProvider } from "@/context/local"
import { SDKProvider } from "@/context/sdk"
import { useSync } from "@/context/sync"
import { decode64 } from "@/utils/base64"
import { Schema } from "effect"
import type { ServerConnection } from "@/context/server"
import { sessionHref } from "@/utils/session-route"
import { useServerSync } from "@/context/server-sync"
import { usePreviewLifecycle } from "@/context/preview-lifecycle"
import { useSDK } from "@/context/sdk"
import { NotFoundPage } from "./error"
import { isNotFoundError } from "./error-description"

export function DirectoryDataProvider(
  props: ParentProps<{
    directory: string | Accessor<string>
    draftID?: string
    server?: Accessor<ServerConnection.Key | undefined>
  }>,
) {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()
  const sync = useSync()
  const language = useLanguage()
  const serverSync = useServerSync()
  const sdk = useSDK()
  const directory = () => (typeof props.directory === "function" ? props.directory() : props.directory)
  const slug = createMemo(() => base64Encode(directory()))
  const href = (sessionID: string) => {
    const server = props.server?.()
    if (server) return sessionHref(server, sessionID)
    return `/${slug()}/session/${sessionID}`
  }

  createEffect(() => {
    // A draft lives at /new-session?draftId=… and has no directory segment to normalize.
    if (props.draftID || props.server?.()) return
    const next = sync().data.path.directory
    if (!next || next === directory()) return
    const path = location.pathname.slice(slug().length + 1)
    navigate(`/${base64Encode(next)}${path}${location.search}${location.hash}`, { replace: true })
  })

  createResource(
    () => params.id,
    (id) =>
      sync()
        .session.sync(id)
        .catch(() => {}),
  )

  createEffect(() => {
    const sessionID = params.id
    if (!sessionID) return
    serverSync().session.pin(sessionID)
    onCleanup(() => serverSync().session.unpin(sessionID))
  })

  // Start preview dev server when a directory is opened
  usePreviewLifecycle(directory)

  // Another user's project answers every request with 404, which the loaders
  // retry and then swallow, leaving a page that never finishes. One direct
  // request tells it apart; a folder nobody owns yet is provisioned, not 404.
  const [foreign, setForeign] = createSignal(false)
  createEffect(() => {
    const dir = directory()
    setForeign(false)
    void sdk()
      .client.path.get({ directory: dir })
      .then(
        () => undefined,
        (error: unknown) => {
          if (dir === directory()) setForeign(isNotFoundError(error))
        },
      )
  })

  return (
    <Show when={!foreign()} fallback={<NotFoundPage inline />}>
      <Show when={directory()} keyed>
        {(directory) => (
          <div class="relative h-full w-full">
            <DataProvider
              data={sync().data}
              directory={directory}
              onNavigateToSession={(sessionID: string) => navigate(href(sessionID))}
              onSessionHref={href}
            >
              <LocalProvider>{props.children}</LocalProvider>
            </DataProvider>
            <Show when={sync().status === "loading"}>
              <div class="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-surface-base/85 backdrop-blur-sm">
                <BrandLogo size="large" class="animate-pulse" />
                <LoaderV2 class="text-text-weak" width={24} height={24} />
                <span class="text-13-regular text-text-weak">Starting project…</span>
              </div>
            </Show>
          </div>
        )}
      </Show>
    </Show>
  )
}

export const ProjectDirString = Schema.String.pipe(Schema.brand("ProjectDirString"))
export type ProjectDirString = Schema.Schema.Type<typeof ProjectDirString>

export function decodeDirectory(dir: string): ProjectDirString | undefined {
  const decoded = decode64(dir)
  if (!decoded) return
  return ProjectDirString.make(decoded)
}

export default function Layout(props: ParentProps) {
  const params = useParams()
  const language = useLanguage()
  const navigate = useNavigate()
  let invalid = ""

  const resolved = createMemo(() => {
    if (!params.dir) return ""
    return decodeDirectory(params.dir) ?? ""
  })

  createEffect(() => {
    const dir = params.dir
    if (!dir) return
    if (resolved()) {
      invalid = ""
      return
    }
    if (invalid === dir) return
    invalid = dir
    showToast({
      variant: "error",
      title: language.t("common.requestFailed"),
      description: language.t("directory.error.invalidUrl"),
    })
    navigate("/", { replace: true })
  })

  return (
    <Show when={resolved()} keyed>
      {(resolved) => (
        <SDKProvider directory={resolved}>
          <DirectoryDataProvider directory={resolved}>{props.children}</DirectoryDataProvider>
        </SDKProvider>
      )}
    </Show>
  )
}
